import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import './CompanyTeamPage.css';

/*
 * The owner's view of their own sales team.
 *
 * Inviting is owner-only, and that is enforced on the server — the form is
 * hidden from non-owners as a courtesy, not as the boundary. Anyone who posts
 * the request directly still gets a 403.
 *
 * The WhatsApp column is the one that carries weight: a rep without a number is
 * skipped entirely when a lead is assigned, so an empty cell is a problem
 * rather than a blank, and is styled that way.
 */
export default function CompanyTeamPage() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [copied, setCopied] = useState(false);
  const [logoMsg, setLogoMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [asOwner, setAsOwner] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [name, setName] = useState('');
  const [wa, setWa] = useState('');
  const [about, setAbout] = useState('');
  const [savingMe, setSavingMe] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [waMsg, setWaMsg] = useState(null);
  const [waBusy, setWaBusy] = useState(false);

  const isOwner = team.find((m) => m.email === user?.email)?.is_owner ?? false;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        apiClient.get('/auth/company/team'),
        apiClient.get('/auth/company'),
      ]);
      const members = Array.isArray(t.data) ? t.data : [];
      setTeam(members);
      setCompany(c.data || null);
      // Seed the form from the server rather than from what was last typed, so
      // a failed save leaves the fields showing what is actually stored.
      const mine = members.find((m) => m.email === user?.email);
      setName(mine?.full_name || '');
      setWa(mine?.whatsapp_number || '');
      setAbout(c.data?.description || '');
    } catch (e) {
      setMsg({ kind: 'err', text: readError(e, 'Could not load your team.') });
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  /* The branded URL needs wildcard DNS to resolve; the path link works today.
     Offer whichever actually reaches the advisor rather than the prettier one
     that might 404. */
  const shareUrl = company?.advisor_url
    || (company?.advisor_path ? `${window.location.origin}${company.advisor_path}` : '');

  async function copyLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Clipboard access is refused outside a secure context and in some
      // browsers. Selecting the text is the honest fallback.
      const el = document.getElementById('advisor-url');
      if (el) { el.focus(); el.select(); }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function saveProfile(e) {
    e.preventDefault();
    setSavingMe(true);
    setProfileMsg(null);
    try {
      const body = { full_name: name, whatsapp_number: wa };
      // Only the owner may send a description at all; sending it as a
      // non-owner is a 403 that would lose their name edit with it.
      if (isOwner) body.description = about;
      const { data } = await apiClient.patch('/auth/company', body);
      setCompany((c) => ({ ...c, ...data }));
      setProfileMsg({ kind: 'ok', text: 'Saved.' });
      load();
    } catch (err) {
      setProfileMsg({ kind: 'err', text: readError(err, 'Could not save that.') });
    } finally {
      setSavingMe(false);
    }
  }

  async function disconnectWhatsApp() {
    /* Confirmed because buyers mid-conversation stop being able to reach the
       brokerage the moment this lands, and nothing warns them. */
    if (!window.confirm(
      'Disconnect WhatsApp? Buyers will no longer be offered it, and anyone '
      + 'mid-conversation will not get a reply.'
    )) return;
    setWaBusy(true);
    setWaMsg(null);
    try {
      const { data } = await apiClient.delete('/auth/company/whatsapp');
      setCompany((c) => ({ ...c, ...data }));
      setWaMsg({ kind: 'ok', text: 'Disconnected.' });
    } catch (err) {
      setWaMsg({ kind: 'err', text: readError(err, 'Could not disconnect.') });
    } finally {
      setWaBusy(false);
    }
  }

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setLogoMsg(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await apiClient.post('/auth/company/logo', form);
      setCompany((c) => ({ ...c, logo_url: data.logo_url }));
      setLogoMsg({ kind: 'ok', text: 'Logo updated. Leads will see it in the chat header.' });
    } catch (err) {
      setLogoMsg({ kind: 'err', text: readError(err, 'Could not upload that image.') });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  useEffect(() => { load(); }, [load]);

  async function invite(e) {
    e.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    setMsg(null);
    try {
      const { data } = await apiClient.post('/auth/company/team/invite', {
        email: email.trim(),
        grants_ownership: asOwner,
      });
      setMsg({ kind: 'ok', text: data?.message || `Invitation sent to ${email}.` });
      setEmail('');
      setAsOwner(false);
      load();
    } catch (e) {
      setMsg({ kind: 'err', text: readError(e, 'Could not send that invitation.') });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="team-page">
      <div className="team-card">
        <h2 className="team-title">Your advisor link</h2>
        <p className="team-desc">
          Put this on your website, or send it to a buyer. It opens a chat that
          answers from your inventory only.
        </p>

        <div className="team-share">
          <input
            id="advisor-url"
            className="team-input team-share-url"
            value={shareUrl}
            readOnly
            onFocus={(e) => e.target.select()}
          />
          <button className="team-btn" type="button" onClick={copyLink} disabled={!shareUrl}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>

        {company?.advisor_url && company?.advisor_path && (
          <p className="team-note">
            Your branded address is <code>{company.advisor_url}</code>. It starts
            working once DNS is pointed at it — until then the link above is the
            one to share.
          </p>
        )}
      </div>

      <div className="team-card">
        <h2 className="team-title">You and your company</h2>
        <p className="team-desc">
          Your name and number are your own to correct. A rep with no WhatsApp
          number is skipped when leads are handed out.
        </p>

        <form className="team-profile" onSubmit={saveProfile}>
          <label className="team-field">
            <span className="team-label">Your name</span>
            <input
              className="team-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ahmed Osman"
            />
          </label>

          <label className="team-field">
            <span className="team-label">Your WhatsApp number</span>
            <input
              className="team-input"
              value={wa}
              onChange={(e) => setWa(e.target.value)}
              placeholder="+20 10 1234 5678"
            />
          </label>

          {isOwner && (
            <label className="team-field team-field-wide">
              <span className="team-label">About your company</span>
              <textarea
                className="team-input team-textarea"
                value={about}
                onChange={(e) => setAbout(e.target.value)}
                rows={4}
                maxLength={600}
                placeholder="Acme Realty has sold in New Cairo since 2012, working mainly with Hyde Park and Mountain View."
              />
              {/* The advisor repeats this to buyers as fact, so the guidance has
                  to say plainly what it is for — not marketing copy. */}
              <span className="team-hint">
                The advisor tells buyers this when they ask who you are, so keep
                it to things that are true: where you work, how long, which
                developers. {600 - about.length} characters left.
              </span>
            </label>
          )}

          <div className="team-profile-actions">
            <button className="team-btn" type="submit" disabled={savingMe}>
              {savingMe ? 'Saving…' : 'Save'}
            </button>
            {profileMsg && (
              <span className={`team-msg ${profileMsg.kind}`}>{profileMsg.text}</span>
            )}
          </div>
        </form>
      </div>

      {isOwner && (
        <div className="team-card">
          <h2 className="team-title">WhatsApp</h2>
          <p className="team-desc">
            Connect your company's WhatsApp and the advisor answers buyers there
            under your own number, with your own inventory. This is a separate
            number from the ones your team use on their phones — a number
            connected here cannot also be used in the WhatsApp app.
          </p>

          {company?.whatsapp_connected ? (
            <>
              <div className="team-wa-row">
                <span className="team-wa-live">Connected</span>
                <span className="team-wa-number">{company.advisor_whatsapp_number}</span>
              </div>
              <p className="team-hint">
                Buyers on your advisor page are offered WhatsApp, and replies
                come from the same advisor they were already talking to.
              </p>
              <button
                className="team-btn team-btn-quiet"
                onClick={disconnectWhatsApp}
                disabled={waBusy}
              >
                {waBusy ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </>
          ) : (
            /* Not a dead button: connecting runs through Meta and cannot be
               faked from here, so this says what is missing instead of
               pretending to start something. */
            <>
              <div className="team-wa-row">
                <span className="team-wa-off">Not connected</span>
              </div>
              <p className="team-hint">
                Connecting is not open yet. When it is, you will approve it
                through Meta and your number appears here — nothing to type in.
              </p>
            </>
          )}

          {waMsg && <div className={`team-msg ${waMsg.kind}`}>{waMsg.text}</div>}
        </div>
      )}

      {isOwner && (
        <div className="team-card">
          <h2 className="team-title">Company logo</h2>
          <p className="team-desc">
            Shown in the chat header of every conversation. PNG, JPEG or SVG,
            under 512 KB, roughly square to 4:1 wide. Without one the header
            shows your company name instead.
          </p>

          <div className="team-logo-row">
            <span className="team-logo-chip">
              {company?.logo_url
                ? <img src={company.logo_url} alt={company?.name} />
                : <span className="team-logo-fallback">{company?.name || 'Your company'}</span>}
            </span>

            <label className="team-btn team-file">
              {uploading ? 'Uploading…' : company?.logo_url ? 'Replace logo' : 'Upload logo'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={uploadLogo}
                disabled={uploading}
              />
            </label>
          </div>

          {logoMsg && <div className={`team-msg ${logoMsg.kind}`}>{logoMsg.text}</div>}
        </div>
      )}

      {isOwner && (
        <div className="team-card">
          <h2 className="team-title">Invite a salesperson</h2>
          <p className="team-desc">
            They get their own account and see the company's inventory and leads.
            No password is sent — the link lets them set their own, and it expires
            in seven days.
          </p>

          <form className="team-form" onSubmit={invite}>
            <div className="team-field">
              <label className="team-label" htmlFor="invite-email">Email</label>
              <input
                id="invite-email"
                className="team-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="sara@yourcompany.com"
                autoComplete="off"
              />
            </div>
            <label className="team-check">
              <input
                type="checkbox"
                checked={asOwner}
                onChange={(e) => setAsOwner(e.target.checked)}
              />
              Make them an owner too
            </label>
            <button className="team-btn" type="submit" disabled={!email.trim() || sending}>
              {sending ? 'Sending…' : 'Send invitation'}
            </button>
          </form>

          {msg && <div className={`team-msg ${msg.kind}`}>{msg.text}</div>}
        </div>
      )}

      <div className="team-card">
        <h2 className="team-title">Your team</h2>
        <p className="team-desc">
          Leads are handed to one person's WhatsApp. Anyone without a number is
          skipped when a lead is assigned.
        </p>

        {loading ? (
          <div className="team-empty">Loading…</div>
        ) : team.length === 0 ? (
          <div className="team-empty">Nobody here yet.</div>
        ) : (
          <table className="team-table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>WhatsApp</th><th>Role</th>
              </tr>
            </thead>
            <tbody>
              {team.map((m) => (
                <tr key={m.email}>
                  <td className="team-name">{m.full_name}</td>
                  <td>{m.email}</td>
                  <td>
                    {m.whatsapp_number
                      ? m.whatsapp_number
                      : <span className="team-missing">No number — leads skip them</span>}
                  </td>
                  <td>
                    <span className={`team-pill ${m.is_owner ? 'owner' : 'sales'}`}>
                      {m.is_owner ? 'Owner' : 'Sales'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* Server messages here are written for the person reading them (rate limits,
   "only the owner can invite"), so prefer them over a generic failure. */
function readError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  return fallback;
}
